import { useCallback } from 'react'
import { useSelector } from 'react-redux'

import { useResetEditingState } from '@/atoms'
import { selectWorkingDayStartSec } from '@/features/domain/territory'
import { updateTimeWindows } from '@/features/domain/traffic'
import { useNotification } from '@/hooks'
import { useAppDispatch } from '@/store'
import { timeWindowUtils } from '@/utils'

import type { TrafficTimeWindows } from '../types'

export const useOnSubmit = () => {
  const stopEditing = useResetEditingState()
  const dispatch = useAppDispatch()
  const toast = useNotification()

  const workingDayStartSec = useSelector(selectWorkingDayStartSec)

  return useCallback(
    async (formValues: TrafficTimeWindows) => {
      try {
        const timeWindows = computeTimeWindows(
          formValues.timeWindows as [uui.domain.rm.TimeWindow?, uui.domain.rm.TimeWindow?],
          workingDayStartSec,
        )

        const thunkResult = await dispatch(updateTimeWindows(timeWindows))

        if (updateTimeWindows.rejected.match(thunkResult)) {
          throw new Error(thunkResult.payload?.message ?? 'Internal error')
        }

        stopEditing()
      } catch (e) {
        toast.error(e.message)
      }
    },
    [dispatch, stopEditing, toast, workingDayStartSec],
  )
}

function computeTimeWindows(
  timeWindows: [uui.domain.rm.TimeWindow?, uui.domain.rm.TimeWindow?],
  workingDayStartSec: number,
) {
  return timeWindows.map((tw: uui.domain.rm.TimeWindow | undefined) => {
    if (!tw) return undefined
    if (timeWindowUtils.isTimeWindowEmpty(tw)) return undefined

    return timeWindowUtils.formatTimeWindowForRPC(
      timeWindowUtils.normalizeTimeWindow(tw, workingDayStartSec),
      workingDayStartSec,
    )
  }) as [uui.domain.rm.TimeWindow?, uui.domain.rm.TimeWindow?]
}
